import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IssueInfoGridColumnDTO } from '../../../../core/interface/issue-info.interface';
import {
  IssuePrior2018RowDTO,
  IssuePrior2018RecordDTO,
} from '../../../../core/interface/issues-2017-2018.interface';
import {
  ISSUE_INFO_ADDITIONAL_COLUMNS,
  ISSUE_2018_COMMON_COLUMNS,
  ISSUE_2018_EXISTING_COLUMNS,
} from '../../../../core/constants/issues-2017-2018.constants';
import {
  AUDIT_TRAIL_TABLE_COLUMNS,
  AUDIT_TRAIL_CONSTANT,
} from '../../../../core/constants/auditTrail.constants';
import { AuditTrailRecordDTO } from '../../../../core/interface/audit-trail.interface';
import { AppModalService } from '../../../../core/services/app-modal.service';

import {
  FooterSummaryRow,
  IncomeCredit2018Total,
} from '../../../../core/interface/all-issues.interface';
import { AppCommonService } from '../../../../core/services/app-common.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastService } from '../../../../core/services/app-toast.service';

@Component({
  selector: 'app-issue-prior-2018',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './issue-prior-2018.html',
  styleUrls: ['./issue-prior-2018.scss'],
})
export class IssuePrior2018 implements OnChanges {
  @Input()
  mode: 'existing' | 'allIssues' = 'existing';
  @Input() action: 'view' | 'edit' = 'view';
  @Input() incomeCredit2018Total!: IncomeCredit2018Total;
  @Input() auditChanges2018Total!: IncomeCredit2018Total;
  @Input() showHideIcons: boolean = false;
  @Input() isExistingHeaderVisible: boolean = false;
  @Output() openLink = new EventEmitter<string>();
  @Output() editRecordEvent = new EventEmitter<number>();
  @Output() deleteClicked = new EventEmitter<IssuePrior2018RecordDTO>();
  @Output() gbcChanged = new EventEmitter<{
    recordId: number;
    value: string;
  }>();
  @Input({ required: true })
  records!: IssuePrior2018RecordDTO[];
  is199Checked: boolean = false;
  isASCChecked: boolean = true;
  footerSummaryData: FooterSummaryRow[] = [];

  readonly footerGridTemplate = `
  minmax(90px, 0.9fr)
  minmax(90px, 0.9fr)
  repeat(6, minmax(75px, 1fr))
  minmax(110px, 1fr)
`;

  get baseGridTemplate(): string {
    if (this.mode === 'allIssues') {
      return `
  minmax(90px, 0.9fr)
  minmax(90px, 0.9fr)
  repeat(6, minmax(75px, 1fr))
  minmax(110px, 1fr)
  minmax(110px, 1fr)
  minmax(110px, 1fr)
`;
    }
    return 'minmax(100px, 0.5fr) repeat(6, minmax(170px, 1fr))';
  }

  private readonly appModalService = inject(AppModalService);
  private readonly commonService = inject(AppCommonService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toastService = inject(ToastService);

  get gridTemplate(): string {
    return this.baseGridTemplate;
  }

  get gridTemplateHeader(): string {
    return this.mode === 'allIssues' ? `${this.baseGridTemplate} 40px` : this.baseGridTemplate;
  }

  get showAdditionalColumns(): boolean {
    return this.mode === 'allIssues';
  }

  get displayColumns(): IssueInfoGridColumnDTO[] {
    if (this.mode === 'allIssues') {
      return [...ISSUE_2018_COMMON_COLUMNS, ...ISSUE_INFO_ADDITIONAL_COLUMNS];
    }

    return ISSUE_2018_EXISTING_COLUMNS;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['incomeCredit2018Total']?.currentValue) {
      this.bindIncomeCredit2018Total(changes['incomeCredit2018Total'].currentValue, false);
    }
    if (changes['auditChanges2018Total']?.currentValue) {
      this.bindIncomeCredit2018Total(changes['auditChanges2018Total'].currentValue, true);
    }
  }

  bindIncomeCredit2018Total(recordData: IncomeCredit2018Total, isAudit = false): void {
    const gbcValue = isAudit
      ? (recordData.audit_GBC ?? recordData.Total_Audit_GBC ?? 0)
      : recordData.gbc;
    const totalGbcValue = isAudit
      ? (recordData.Total_Audit_GBC ?? recordData.audit_GBC ?? 0)
      : recordData.total_GBC;

    this.footerSummaryData = [
      {
        label: 'TI b/f 199',
        values: [
          recordData.total_TI_US,
          recordData.total_TI_General,
          recordData.total_TI_FOGEI,
          recordData.total_TI_FORI,
          recordData.total_TI_Passive,
          recordData.total_TI_CapitalGain,
          recordData.total_TI,
        ],
      },
      {
        label: '199 Deduct',
        values: [
          recordData.total_D199_US,
          recordData.total_D199_General,
          recordData.total_D199_FOGEI,
          recordData.total_D199_FORI,
          recordData.total_D199_Passive,
          null,
          recordData.total_D199,
        ],
      },
      {
        label: 'FTC',
        values: [
          null,
          recordData.total_FTC_General,
          recordData.total_FTC_FOGEI,
          recordData.total_FTC_FORI,
          recordData.total_FTC_Passive,
          null,
          recordData.total_FTC,
        ],
      },
      {
        label: 'GBC',
        values: [gbcValue, null, null, null, null, null, totalGbcValue],
      },
    ];
  }

  getCellValue(row: IssuePrior2018RowDTO, key: string): string {
    return String(row[key as keyof IssuePrior2018RowDTO] ?? '');
  }

  showAudit(record: any): void {
    this.commonService
      .getAuditTrailById(record.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((data: AuditTrailRecordDTO[]) => {
        const columns = AUDIT_TRAIL_TABLE_COLUMNS;
        this.appModalService.openAudit(AUDIT_TRAIL_CONSTANT.auditTrail, columns, data);
      });
  }

  editRecord(row: any) {
    if (row.isFilled) {
      const msg = `This AR is filed, you are not allowed to update the adjustment.`;
      this.toastService.error(msg);
    } else {
      this.appModalService.updateEditIssueStatus(true);
      this.editRecordEvent.emit(row.id);
    }
  }

  /** ISSUE_DELETE_CANCEL */
  deleteRecord(row: IssuePrior2018RecordDTO): void {
    if (!this.commonService.canIssueBeCancelled()) {
      return;
    }
    if (row.isFilled) {
      const msg = `This AR is filed, you are not allowed to cancel the adjustment.`;
      this.toastService.error(msg);
    } else {
      this.deleteClicked.emit(row);
    }
  }

  updateCell(row: IssuePrior2018RowDTO, key: string, value: string): void {
    const nextValue = String(value ?? '');
    if (!this.isIntegerValue(nextValue)) {
      return;
    }
    row[key as keyof IssuePrior2018RowDTO] = nextValue as never;
    if (row.empty === 'GBC' && key === 'us') {
      const record = this.records.find((r) => r.rows.includes(row));
      this.gbcChanged.emit({
        recordId: record?.id ?? 0,
        value: nextValue,
      });
    }
  }

  allowNumericDecimal(event: KeyboardEvent, currentValue: string): void {
    const key = event.key;
    if (['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) {
      return;
    }

    if (/^\d$/.test(key)) {
      return;
    }

    if (key === '-') {
      const input = event.target as HTMLInputElement | null;
      const selectionStart = input?.selectionStart ?? currentValue.length;
      const selectionEnd = input?.selectionEnd ?? currentValue.length;
      const nextValue = this.buildNextValue(currentValue, key, selectionStart, selectionEnd);
      if (nextValue === '-' || /^-?\d*$/.test(nextValue)) {
        if (selectionStart === 0 && !nextValue.slice(1).includes('-')) {
          return;
        }
      }
      event.preventDefault();
      return;
    }

    event.preventDefault();
  }

  handleIntegerPaste(event: ClipboardEvent, row: IssuePrior2018RowDTO, key: string): void {
    const pasteText = event.clipboardData?.getData('text') ?? '';
    const input = event.target as HTMLInputElement | null;
    const currentValue = this.getCellValue(row, key);
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;
    const nextValue = this.buildNextValue(currentValue, pasteText, selectionStart, selectionEnd);

    if (!this.isIntegerValue(nextValue)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.updateCell(row, key, nextValue);
  }

  handleIntegerDrop(event: DragEvent, row: IssuePrior2018RowDTO, key: string): void {
    const dropText = event.dataTransfer?.getData('text') ?? '';
    const input = event.target as HTMLInputElement | null;
    const currentValue = this.getCellValue(row, key);
    const selectionStart = input?.selectionStart ?? currentValue.length;
    const selectionEnd = input?.selectionEnd ?? currentValue.length;
    const nextValue = this.buildNextValue(currentValue, dropText, selectionStart, selectionEnd);

    if (!this.isIntegerValue(nextValue)) {
      event.preventDefault();
      return;
    }

    event.preventDefault();
    this.updateCell(row, key, nextValue);
  }

  private isIntegerValue(value: string): boolean {
    return /^-?\d*$/.test(value);
  }

  private buildNextValue(
    currentValue: string,
    insertedText: string,
    selectionStart: number,
    selectionEnd: number,
  ): string {
    return `${currentValue.slice(0, selectionStart)}${insertedText}${currentValue.slice(selectionEnd)}`;
  }

  getSpDocLink(value: string | null | undefined) {
    return this.commonService.getSpDocLink(value);
  }

  navigateToLink(value: string): void {
    const spDocLink = this.getSpDocLink(value);
    if (spDocLink.url) {
      this.openLink.emit(spDocLink.url);
    } else if (!spDocLink.hasSeparator && spDocLink.displayText) {
      this.toastService.error('Invalid Doc Link');
    }
  }

  onLinkKeydown(event: KeyboardEvent, value: string): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }
    event.preventDefault();
    this.navigateToLink(value);
  }
}
